import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import LoadingSpinner from './LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SharedItemDetail: React.FC<{ user: any }> = ({ user }) => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchItem = useCallback(async () => {
    try {
      const endpoint = type === 'questions' ? `/questions/${id}` : `/public/${type}/${id}`;
      const res = await axios.get(`${API_URL}${endpoint}`);
      setItem(res.data);
    } catch (err) {
      console.error('Error fetching shared item:', err);
    }
  }, [type, id]);

  const fetchComments = useCallback(async () => {
    try {
      const endpoint = type === 'questions' ? `/public/questions/${id}/comments` : `/public/${type}/${id}/comments`;
      const res = await axios.get(`${API_URL}${endpoint}`);
      setComments(res.data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  }, [type, id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchItem(), fetchComments()]);
      setLoading(false);
    };
    loadData();
  }, [fetchItem, fetchComments]);

  const handleLike = async () => {
    if (!user) {
      alert("You must be logged in to like shared wisdom.");
      return;
    }
    try {
      let likePayload = {};
      if (type === 'spells') likePayload = { spell_id: parseInt(id!) };
      else if (type === 'grimoire') likePayload = { grimoire_id: parseInt(id!) };
      else if (type === 'journal') likePayload = { journal_id: parseInt(id!) };
      else likePayload = { question_id: parseInt(id!) };

      await axios.post(`${API_URL}/likes`, 
        likePayload,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchItem();
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      alert("You must be logged in to comment.");
      return;
    }
    if (!newComment.trim()) return;

    try {
      await axios.post(`${API_URL}/comments`, 
        { type, id: parseInt(id!), content: newComment },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setNewComment('');
      fetchComments();
    } catch (err) {
      console.error('Error adding comment:', err);
    }
  };

  if (loading) return <LoadingSpinner message="Consulting the scroll..." />;
  if (!item) return <div className="content"><h2>Item not found.</h2><button onClick={() => navigate(-1)}>Go Back</button></div>;

  return (
    <div className="content">
      <button onClick={() => navigate(-1)} style={{ marginBottom: '1.5rem' }}>&larr; Back to Feed</button>
      
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Link to={`/profile/${item.username}`} className="badge" style={{ textDecoration: 'none', color: 'inherit', border: '1px solid var(--gold)' }}>
            Shared by {item.username}
          </Link>
          <button 
            onClick={handleLike} 
            style={{ padding: '0.4rem 0.8rem', fontSize: '1rem', background: 'none', border: '1px solid var(--gold)' }}
          >
            ⭐ {item.like_count}
          </button>
        </div>
        
        <h1 style={{ color: 'var(--gold)', margin: '1rem 0' }}>{item.name || item.title || `Journal Entry - ${item.date}`}</h1>
        
        <div style={{ marginBottom: '1.5rem' }}>
          {item.lunar_phase && <span className="badge">🌙 {item.lunar_phase}</span>}
          {item.category && <span className="badge" style={{ backgroundColor: 'var(--mystic-purple)' }}>{item.category}</span>}
        </div>

        {type === 'spells' && (
          <div className="spell-details">
            <p><strong>Intent:</strong> {item.intent}</p>
            <p><strong>Ingredients:</strong> {item.ingredients}</p>
            <p><strong>Timing:</strong> {item.timing}</p>
            {item.results && (
                <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid var(--gold)', borderRadius: '4px' }}>
                    <strong>Results & Outcomes:</strong>
                    <p>{item.results}</p>
                </div>
            )}
          </div>
        )}

        {(type === 'grimoire' || type === 'journal') && (
           <div className="rich-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }} />
        )}

        {type === 'questions' && (
           <div className="rich-content">
             <p style={{ whiteSpace: 'pre-wrap' }}>{item.content}</p>
           </div>
        )}
      </div>

      <div className="comments-section" style={{ marginTop: '3rem' }}>
        <h3>Circle Discussion</h3>
        
        {user ? (
          <form onSubmit={handleAddComment} style={{ marginBottom: '2rem' }}>
            <textarea 
              placeholder="Add your insight, question, or encouragement..." 
              value={newComment} 
              onChange={e => setNewComment(e.target.value)}
              rows={3}
              required
            />
            <button type="submit">Share Comment</button>
          </form>
        ) : (
          <p style={{ marginBottom: '2rem' }}><em>Please <Link to="/auth" style={{ color: 'var(--gold)' }}>login</Link> to join the discussion.</em></p>
        )}

        <div className="comments-list">
          {comments.length > 0 ? (
            comments.map((comment: any) => (
              <div key={comment.id} className="card" style={{ borderLeft: '4px solid var(--gold)', marginBottom: '1rem', padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Link to={`/profile/${comment.username}`} style={{ color: 'var(--gold)', fontWeight: 'bold', textDecoration: 'none' }}>
                    {comment.username}
                  </Link>
                  <small style={{ opacity: 0.7 }}>{new Date(comment.created_at).toLocaleDateString()}</small>
                </div>
                <p style={{ margin: 0 }}>{comment.content}</p>
              </div>
            ))
          ) : (
            <p style={{ opacity: 0.7 }}>No comments yet. Start the conversation!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedItemDetail;
